import { api } from './authService';

export type Department = 'Custo' | 'Administrativo' | 'Comercial' | 'P&D';

export const DEPARTMENTS: Department[] = ['Custo', 'Administrativo', 'Comercial', 'P&D'];

export interface AccountNode {
  account: string;
  full_path: string;
  balances: Record<string, number>;
  children: AccountNode[];
  department?: Department | null;
  department_inherited?: boolean;
}

export interface BalanceResponse {
  accounts: AccountNode[];
  timestamp: string;
}

export interface RegisterEntry {
  date: string;
  payee: string;
  accounts?: { account: string; amount: number; commodity: string }[];
  amount: number;
  commodity: string;
  running_balance: Record<string, number>;
  status: string;
  docs?: string[];
  postings?: { account: string; amount: number; commodity: string }[];
  txn_id?: string;
}

export interface RegisterResponse {
  account: string;
  entries: RegisterEntry[];
  total: number;
  has_more: boolean;
  timestamp: string;
}

export async function getBalance(params?: {
  account?: string;
  begin?: string;
  end?: string;
}): Promise<BalanceResponse> {
  const res = await api.get<BalanceResponse>('/balance', { params });
  return res.data;
}

export async function getRegister(params: {
  account: string;
  begin?: string;
  end?: string;
  offset?: number;
  limit?: number;
}): Promise<RegisterResponse> {
  const res = await api.get<RegisterResponse>('/register', { params });
  return res.data;
}

export async function getRegisterMulti(params: {
  accounts: string[];
  begin?: string;
  end?: string;
  offset?: number;
  limit?: number;
}): Promise<RegisterResponse> {
  const sp = new URLSearchParams();
  for (const acc of params.accounts) sp.append('account', acc);
  if (params.begin) sp.append('begin', params.begin);
  if (params.end) sp.append('end', params.end);
  if (params.offset != null) sp.append('offset', String(params.offset));
  if (params.limit != null) sp.append('limit', String(params.limit));
  const res = await api.get<RegisterResponse>(`/register?${sp.toString()}`);
  return res.data;
}

export async function getAccounts(): Promise<{ accounts: string[]; total: number }> {
  const res = await api.get<{ accounts: string[]; total: number }>('/accounts');
  return res.data;
}

export interface MonthlyHistoryEntry {
  year: number;
  month: number;
  label: string;
  receita: number;
  despesas: number;
}

export interface MonthlyHistoryResponse {
  months: MonthlyHistoryEntry[];
  timestamp: string;
}

export async function getMonthlyHistory(params: {
  year: number;
  month: number;
  months?: number;
}): Promise<MonthlyHistoryResponse> {
  const res = await api.get<MonthlyHistoryResponse>('/balance/monthly-history', { params });
  return res.data;
}

// Account management
export async function getDeclaredAccounts(): Promise<{ accounts: string[]; total: number }> {
  const res = await api.get<{ accounts: string[]; total: number }>('/accounts/declared');
  return res.data;
}

export async function createAccount(data: { name: string; commodity?: string }): Promise<{ account: string }> {
  const res = await api.post<{ account: string }>('/accounts', data);
  return res.data;
}

export async function updateAccount(name: string, data: { new_name?: string; commodity?: string }): Promise<{ account: string }> {
  const res = await api.put<{ account: string }>(`/accounts/${encodeURIComponent(name)}`, data);
  return res.data;
}

export async function deleteAccount(name: string): Promise<void> {
  await api.delete(`/accounts/${encodeURIComponent(name)}`);
}

export async function updateAccountDepartment(
  name: string,
  department: Department | null,
): Promise<{ account: string; department: Department | null }> {
  const res = await api.patch<{ account: string; department: Department | null }>(
    `/accounts/${encodeURIComponent(name)}`,
    { department },
  );
  return res.data;
}

// Transactions
export interface Posting {
  account: string;
  amount: number | null;
  commodity: string | null;
}

export interface Transaction {
  id: string;
  date: string;
  payee: string;
  postings: Posting[];
  tags: Record<string, string>;
  file: string;
  doc_count?: number;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
}

export async function getTransactions(params?: {
  begin?: string;
  end?: string;
  account?: string;
  description?: string;
}, signal?: AbortSignal): Promise<TransactionsResponse> {
  const res = await api.get<TransactionsResponse>('/transactions', { params, signal });
  return res.data;
}

export async function getTransaction(id: string): Promise<Transaction> {
  const res = await api.get<Transaction>(`/transactions/${id}`);
  return res.data;
}

export async function createTransaction(data: {
  date: string;
  payee: string;
  postings: Posting[];
  tags?: Record<string, string>;
}): Promise<Transaction> {
  const res = await api.post<Transaction>('/transactions', data);
  return res.data;
}

export async function updateTransaction(id: string, data: {
  date: string;
  payee: string;
  postings: Posting[];
  tags?: Record<string, string>;
}): Promise<Transaction> {
  const res = await api.put<Transaction>(`/transactions/${id}`, data);
  return res.data;
}

export async function deleteTransaction(id: string): Promise<void> {
  await api.delete(`/transactions/${id}`);
}

// Vincula dois lancamentos como UMA transferencia banco<->banco. Funde as duas
// pernas bancarias num unico lancamento, preservando os fingerprints do MCP
// (dedup intacto). Retorna o lancamento fundido.
export async function linkTransfer(data: {
  txn_id_a: string;
  txn_id_b: string;
}): Promise<Transaction> {
  const res = await api.post<Transaction>('/transactions/link-transfer', data);
  return res.data;
}

// Cambio
export interface CambioSummary {
  total_operations: number;
  average_rate: number;
  total_usd: number;
  total_brl: number;
  platforms: Record<string, { count: number; total_brl: number }>;
}

export async function getCambioSummary(): Promise<CambioSummary> {
  const res = await api.get<CambioSummary>('/cambio/summary');
  return res.data;
}

export interface CashflowEntry {
  year: number;
  month: number;
  label: string;
  entradas: number;
  saidas: number;
  entradas_operacional: number;
  saidas_operacional: number;
  entradas_financiamento: number;
  saidas_financiamento: number;
  entradas_socios: number;
  saidas_socios: number;
  entradas_investimento: number;
  saidas_investimento: number;
  saldo_acumulado: number;
  projected?: boolean;
}

export interface CashflowResponse {
  months: CashflowEntry[];
  timestamp: string;
}

export async function getCashflow(params?: {
  begin?: string;
  end?: string;
}): Promise<CashflowResponse> {
  const res = await api.get<CashflowResponse>('/cashflow', { params });
  return res.data;
}

export interface CashflowTransaction {
  date: string;
  payee: string;
  amount: number;
  contras: string[];
  running_balance: number;
}

export interface CashflowTransactionsResponse {
  transactions: CashflowTransaction[];
  total: number;
}

export async function getCashflowTransactions(params: {
  year: number;
  month: number;
  category: string;
  direction: string;
}): Promise<CashflowTransactionsResponse> {
  const res = await api.get<CashflowTransactionsResponse>('/cashflow/transactions', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Monthly Projection
// ---------------------------------------------------------------------------

export interface ProjectionItem {
  account: string;
  expected_amount: number;
  typical_day: number;
  frequency: number;
  direction: string;
  payee: string;
  realized: boolean;
  realized_amount: number | null;
  realized_transactions: { date: string; amount: number; payee: string }[];
}

export interface ProjectionResponse {
  year: number;
  month: number;
  projections: ProjectionItem[];
  summary: {
    opening_balance: number;
    total_expected_entradas: number;
    total_expected_saidas: number;
    total_realized_entradas: number;
    total_realized_saidas: number;
  };
  timestamp: string;
}

export async function getProjection(params: {
  year: number;
  month: number;
}): Promise<ProjectionResponse> {
  const res = await api.get<ProjectionResponse>('/projection', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Cash Flow Forecast (13-week rolling direct)
// ---------------------------------------------------------------------------

export interface CashflowForecastEntry {
  date: string;
  amount: number;
  source: 'realized' | 'loan' | 'historical' | 'provisioned';
  account: string;
  group: 'operacional' | 'investimento' | 'financiamento';
  description: string;
}

export interface CashflowForecastWeek {
  week_start: string;
  week_end: string;
  opening_balance: number;
  inflows: CashflowForecastEntry[];
  outflows: CashflowForecastEntry[];
  total_inflows: number;
  total_outflows: number;
  net: number;
  closing_balance: number;
}

export interface CashflowForecastResponse {
  generated_at: string;
  today: string;
  weeks_count: number;
  lookback_months: number;
  opening_balance: number;
  weeks: CashflowForecastWeek[];
  summary: {
    total_inflows: number;
    total_outflows: number;
    net: number;
    min_balance: { date: string | null; amount: number | null };
    by_group: Record<string, { inflows: number; outflows: number }>;
  };
}

export async function getCashflowForecast(params?: {
  weeks?: number;
  lookback_months?: number;
}): Promise<CashflowForecastResponse> {
  const res = await api.get<CashflowForecastResponse>('/cashflow-forecast', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// DRE (Demonstrativo de Resultado)
// ---------------------------------------------------------------------------

export interface DREEntry {
  year: number;
  month: number;
  label: string;
  receita_servicos: number;
  receita_financeira: number;
  receitas: number;
  despesas_operacionais: number;
  despesas_financeiras: number;
  despesas: number;
  resultado_operacional: number;
  resultado_financeiro: number;
  resultado: number;
  resultado_acumulado: number;
}

export interface DREResponse {
  months: DREEntry[];
  timestamp: string;
}

export async function getDRE(params?: {
  begin?: string;
  end?: string;
}): Promise<DREResponse> {
  const res = await api.get<DREResponse>('/dre', { params });
  return res.data;
}

export interface DRETransaction {
  date: string;
  payee: string;
  account: string;
  amount: number;
  running_balance: number;
}

export interface DRETransactionsResponse {
  transactions: DRETransaction[];
  total: number;
}

export async function getDRETransactions(params: {
  year: number;
  month: number;
  category: string;
}): Promise<DRETransactionsResponse> {
  const res = await api.get<DRETransactionsResponse>('/dre/transactions', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Taxes
// ---------------------------------------------------------------------------

export interface TributoDetail {
  percentual: number;
  valor: number;
}

export interface TaxesResponse {
  regime: string;
  periodo: string;
  rbt12: number | null;
  faturamento: number;
  faixa: number | null;
  aliquota_nominal: number | null;
  deducao: number | null;
  aliquota_efetiva: number;
  valor_das: number | null;
  valor_total?: number | null;
  tributos: Record<string, TributoDetail>;
}

export interface TaxHistoryEntry {
  year: number;
  month: number;
  label: string;
  faturamento: number;
  carga_tributaria: number;
  percentual: number;
}

export interface TaxHistoryResponse {
  months: TaxHistoryEntry[];
}

export async function getTaxes(period: string): Promise<TaxesResponse> {
  const res = await api.get<TaxesResponse>('/taxes', { params: { period } });
  return res.data;
}

export async function getTaxHistory(params: {
  year: number;
  month: number;
  months?: number;
}): Promise<TaxHistoryResponse> {
  const res = await api.get<TaxHistoryResponse>('/taxes/history', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface DREReportResponse {
  periodo: { begin: string; end: string };
  receita_bruta: number;
  impostos_receita: { total: number; detalhamento: string[] };
  receita_liquida: number;
  despesas_operacionais: {
    total: number;
    categorias: { account: string; label: string; value: number }[];
  };
  lucro_operacional: number;
  irpj_csll: number;
  lucro_liquido: number;
  timestamp: string;
}

export async function getDREReport(params: {
  begin: string;
  end: string;
}): Promise<DREReportResponse> {
  const res = await api.get<DREReportResponse>('/reports/dre', { params });
  return res.data;
}

export interface BalanceSheetSection {
  total: number;
  contas: { account: string; label: string; value: number }[];
}

export interface BalanceSheetResponse {
  data: string;
  ativos: BalanceSheetSection;
  passivo: BalanceSheetSection;
  patrimonio_liquido: BalanceSheetSection;
  timestamp: string;
}

export interface TrialBalanceAccount {
  account: string;
  label: string;
  debito: number;
  credito: number;
  saldo: number;
}

export interface TrialBalanceResponse {
  periodo: string;
  contas: TrialBalanceAccount[];
  total_debito: number;
  total_credito: number;
  timestamp: string;
}

export async function getBalanceSheet(params: {
  date: string;
}): Promise<BalanceSheetResponse> {
  const res = await api.get<BalanceSheetResponse>('/reports/balance-sheet', { params });
  return res.data;
}

export async function getTrialBalance(params: {
  period: string;
}): Promise<TrialBalanceResponse> {
  const res = await api.get<TrialBalanceResponse>('/reports/trial-balance', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

export interface ParsedTransaction {
  date: string;
  description: string;
  amount: number;
  needs_review?: boolean;
  review_reason?: string;
  review_hint?: string;
  duplicate_of?: string;
  duplicate_kind?: string;
  duplicate_date?: string;
  ofx_fitid?: string;
  ofx_account?: string;
  dedup_fingerprint?: string;
  suggested_category?: string;
  original_payee?: string;
}

export interface ParseResponse {
  transactions: ParsedTransaction[];
  total: number;
  bulk_duplicate?: boolean;
  fitid_skip_count?: number;
}

export async function parseImportFile(
  file: File,
  options: {
    format: string;
    separator?: string;
    date_col?: number;
    description_col?: number;
    amount_col?: number;
    date_format?: string;
    skip_header?: boolean;
    source_account?: string;
  },
): Promise<ParseResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('format', options.format);
  if (options.separator) formData.append('separator', options.separator);
  if (options.date_col !== undefined) formData.append('date_col', String(options.date_col));
  if (options.description_col !== undefined) formData.append('description_col', String(options.description_col));
  if (options.amount_col !== undefined) formData.append('amount_col', String(options.amount_col));
  if (options.date_format) formData.append('date_format', options.date_format);
  if (options.skip_header !== undefined) formData.append('skip_header', String(options.skip_header));
  if (options.source_account) formData.append('source_account', options.source_account);
  const res = await api.post<ParseResponse>('/transactions/import/parse', formData);
  return res.data;
}

export interface ImportTransaction {
  date: string;
  description: string;
  amount: number;
  category: string;
  source_account: string;
  original_payee?: string;
  ofx_fitid?: string;
  ofx_account?: string;
  dedup_fingerprint?: string;
  duplicate_kind?: string;
  duplicate_of?: string;
  dedup_acked?: boolean;
}

export interface CommitResponse {
  created: number;
  errors: { index: number; error: string }[];
  transactions: { date: string; payee: string }[];
  skipped?: {
    date: string;
    description: string;
    amount: number;
    reason: string;
    duplicate_of?: string;
    duplicate_kind?: string;
  }[];
}

export async function commitImport(transactions: ImportTransaction[]): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit', { transactions });
  return res.data;
}

// ============================================================
// Novo fluxo unificado de Ingestão via Pendentes
// (Fase 1.3 — notas/plano-importacao-unificada-pendentes.md)
// ============================================================

export interface IngestOfxResponse {
  total: number;
  ingested: number;
  skipped: number;
  auto_committed: number;
  stay_pending: number;
  needs_review: number;
  pipeline_errors?: number;
  pipeline_error_msg?: string;
  saldo_info?: {
    saldo_anterior: number;
    saldo_final_pdf: number;
    saldo_final_calculado: number;
    ok: boolean;
  } | null;
  pending_ids: string[];
  ingest_errors: { txn: unknown; error: string }[];
}

export async function ingestOfx(
  file: File,
  options: { source_account: string; auto_classify?: boolean },
): Promise<IngestOfxResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('source_account', options.source_account);
  formData.append('auto_classify', String(options.auto_classify !== false));
  const res = await api.post<IngestOfxResponse>('/ingest/ofx', formData);
  return res.data;
}

export interface IngestHoleriteResponse {
  competencia: string;
  total_funcionarios: number;
  created: number;
  skipped: number;
  pending_ids: string[];
  errors: { nome: string; error: string }[];
}

export async function ingestHoleritePdf(file: File): Promise<IngestHoleriteResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<IngestHoleriteResponse>('/ingest/holerite-pdf', formData);
  return res.data;
}

export interface IngestEsocialFolhaResponse {
  per_apur: string;
  total_cpfs: number;
  created: number;
  skipped: number;
  no_mapping: string[];
  pending_ids: string[];
  errors: { cpf: string; error: string }[];
}

export async function ingestEsocialFolha(
  per_apur: string,
  cpf_nome_map?: Record<string, [string, string]>,
): Promise<IngestEsocialFolhaResponse> {
  const res = await api.post<IngestEsocialFolhaResponse>('/ingest/esocial-folha', {
    per_apur,
    cpf_nome_map,
  });
  return res.data;
}

export interface IngestEsocialCppResponse {
  per_apur: string;
  total_files: number;
  created: number;
  skipped: number;
  pending_ids: string[];
  errors: { key: string; error: string }[];
}

export async function ingestEsocialCpp(per_apur: string): Promise<IngestEsocialCppResponse> {
  const res = await api.post<IngestEsocialCppResponse>('/ingest/esocial-cpp', { per_apur });
  return res.data;
}

export async function runPendingPipeline(source?: string): Promise<{
  total: number; auto_committed: number; stay_pending: number; needs_review: number; errors: number;
}> {
  const params = source ? { source } : {};
  const res = await api.post('/ingest/run-pipeline', null, { params });
  return res.data;
}

// ============================================================
// Tarefas (warnings / pendências auto-computed)
// ============================================================

export interface Tarefa {
  id: string;
  'tarefa-id': string;
  date: string;
  payee: string;
  title: string;
  status_account: string;
  tags: Record<string, string>;
}

export interface TarefasResponse {
  tarefas: Tarefa[];
}

export interface TarefasStats {
  abertas: number;
  adiadas: number;
  by_severity: Record<string, number>;
  overdue: number;
}

export async function listTarefas(
  status: string = 'open',
  severity?: string,
): Promise<TarefasResponse> {
  const params: Record<string, string> = { status };
  if (severity) params.severity = severity;
  const res = await api.get<TarefasResponse>('/tarefas', { params });
  return res.data;
}

export async function getTarefasStats(): Promise<TarefasStats> {
  const res = await api.get<TarefasStats>('/tarefas/stats');
  return res.data;
}

export async function refreshTarefasComputed(): Promise<{
  checks: number;
  created: number;
  skipped: number;
  reopened: number;
  recreated: number;
  errors: number;
}> {
  const res = await api.post('/tarefas/refresh-computed');
  return res.data;
}

export async function markTarefaDone(
  tarefaId: string,
  opts: { resolved_by_txn_ids: string[]; resolved_reason?: string },
): Promise<Tarefa> {
  const res = await api.patch<Tarefa>(`/tarefas/${tarefaId}/done`, opts);
  return res.data;
}

export async function dismissTarefa(tarefaId: string, reason: string = ''): Promise<Tarefa> {
  const res = await api.patch<Tarefa>(`/tarefas/${tarefaId}/dismiss`, { reason });
  return res.data;
}

export async function snoozeTarefa(tarefaId: string, until: string): Promise<Tarefa> {
  const res = await api.patch<Tarefa>(`/tarefas/${tarefaId}/snooze`, { until });
  return res.data;
}

export async function createManualTarefa(tarefa: {
  title: string;
  severity?: string;
  due_date?: string;
  description?: string;
  action_url?: string;
  action_label?: string;
  tags?: string[];
}): Promise<Tarefa> {
  const res = await api.post<Tarefa>('/tarefas', tarefa);
  return res.data;
}

export interface DedupReviewEntry {
  fingerprint: string;
  date: string;
  payee: string;
  account: string;
  amount: number;
  acked: boolean;
  distinct_from: string;
}

export interface DedupReviewGroup {
  fingerprint: string;
  entries: DedupReviewEntry[];
}

export async function getDedupReview(): Promise<{ pending: DedupReviewGroup[] }> {
  const res = await api.get<{ pending: DedupReviewGroup[] }>('/transactions/import/dedup-review');
  return res.data;
}

export async function resolveDedupReview(fingerprint: string, action: 'distinct'): Promise<{
  updated: number;
  fingerprint: string;
  action: string;
}> {
  const res = await api.post('/transactions/import/dedup-review/resolve', { fingerprint, action });
  return res.data;
}

export interface DASTributo {
  codigo: string;
  nome: string;
  account: string;
  valor: number;
}

export interface DASData {
  periodo: string;
  data_vencimento: string;
  valor_total: number;
  tributos: DASTributo[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface CDBData {
  produto: string;
  data_aplicacao: string;
  data_resgate: string;
  valor_aplicado: number;
  rendimento_bruto: number;
  iof: number;
  ir: number;
  valor_resgate_liquido: number;
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export async function commitCDBImport(data: {
  date: string;
  description: string;
  postings: { account: string; amount: number; commodity: string }[];
}): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit-cdb', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// NF XML Import
// ---------------------------------------------------------------------------

export interface NFXmlData {
  tipo: 'nfe' | 'nfse';
  numero: string;
  data_emissao: string;
  competencia: string;
  valor_total: number;
  cnpj_emitente: string;
  nome_emitente: string;
  cnpj_destinatario: string;
  nome_destinatario: string;
  descricao: string;
  cliente: string;
  modalidade?: 'pre_recebimento' | 'pos_recebimento' | '';
  conta_receita?: string;
  moeda_servico?: string;
  valor_servico_moeda?: number;
  has_provisao: boolean;
  has_adiantamento: boolean;
  adiantamento_balance?: number;
  competencia_estimada?: boolean;
  warnings?: string[];
  // Structured NFS-e metadata (SPED parser)
  data_emissao_completa?: string;
  valor_liquido?: number;
  iss_retido?: string;
  municipio_incidencia?: string;
  codigo_tributario?: string;
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
  provisao_postings: { account: string; amount: number; commodity: string }[];
}

export async function commitNFImport(data: {
  date: string;
  description: string;
  postings: { account: string; amount: number; commodity: string }[];
  provisao_postings?: { account: string; amount: number; commodity: string }[];
  adiantamento_postings?: { account: string; amount: number; commodity: string }[];
  xml_filename?: string;
  xml_content_base64?: string;
  nfse_numero?: string;
  nfse_competencia?: string;
  nfse_emitida_em?: string;
  nfse_tomador_cnpj?: string;
  nfse_tomador_nome?: string;
  nfse_valor_bruto?: number;
  nfse_valor_liquido?: number;
  nfse_iss_retido?: string;
  nfse_municipio_incidencia?: string;
  nfse_cod_tributario?: string;
}): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit-nf', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// CEF Loan Import
// ---------------------------------------------------------------------------

export interface CEFLoanCommitResponse {
  total_parcelas_pg: number;
  loan_account: string;
  inicial: string | null;
  reclassificadas: { parcela: number; id: string; valor: number }[];
  competencia_criadas: { parcela: number; data: string; juros_encargos: number }[];
  historicas_skip: { parcela: number; vencimento: string; valor: number }[];
  ja_classificadas_skip: { parcela: number }[];
  errors: { parcela?: number; step?: string; error: string }[];
}

export async function commitCEFLoanImport(data: {
  contrato_numero: string;
  contrato_produto: string;
  contrato_valor?: number;
  parcelas: {
    numero: number;
    vencimento: string;
    amortizacao: number;
    juros: number;
    encargos: number;
    valor_total_pago: number;
    saldo_devedor: number;
    status: string;
  }[];
}): Promise<CEFLoanCommitResponse> {
  const res = await api.post<CEFLoanCommitResponse>('/transactions/import/commit-cef-loan', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Payroll Import
// ---------------------------------------------------------------------------

export interface PayrollFuncionario {
  nome: string;
  tipo: string;
  vencimentos: number;
  descontos: number;
  liquido: number;
  inss: number;
  irrf: number;
  fgts: number;
}

export interface PayrollData {
  competencia: string;
  funcionarios: PayrollFuncionario[];
  transactions: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  }[];
}

export interface FGTSFuncionario {
  nome: string;
  matricula: string;
  remuneracao: number;
  fgts: number;
}

export interface FGTSRelatorioData {
  competencia: string;
  total_fgts: number;
  funcionarios: FGTSFuncionario[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface DARFTributo {
  codigo: string;
  nome: string;
  valor: number;
  account_despesa: string;
  account_passivo: string;
}

export interface DARFFederalData {
  competencia: string;
  valor_total: number;
  tributos: DARFTributo[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface FGTSGuiaData {
  competencia: string;
  valor_total: number;
  data_vencimento: string;
}

export interface LiquidosFuncionario {
  nome: string;
  codigo: string;
  valor: number;
}

export interface LiquidosData {
  competencia: string;
  funcionarios: LiquidosFuncionario[];
  total: number;
}

export interface PayrollCommitRequest {
  competencia: string;
  transactions: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  }[];
  funcionarios?: PayrollFuncionario[];
}

export async function commitPayrollImport(data: PayrollCommitRequest): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit-payroll', data);
  return res.data;
}

export interface CrossValidateRequest {
  holerite?: PayrollData | null;
  fgts_relatorio?: FGTSRelatorioData | null;
  liquidos?: LiquidosData | null;
  fgts_guia?: FGTSGuiaData | null;
}

export async function validatePayroll(data: CrossValidateRequest): Promise<{ warnings: string[] }> {
  const res = await api.post<{ warnings: string[] }>('/transactions/import/validate-payroll', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export interface ReconciliationSummary {
  account: string;
  cleared_balance: number;
  cleared_count: number;
  pending_balance: number;
  pending_count: number;
  uncleared_balance: number;
  uncleared_count: number;
  expected_balance: number;
}

export interface ReconciliationPosting {
  date: string;
  payee: string;
  amount: number;
  commodity: string;
  status: 'uncleared' | 'pending' | 'cleared';
  file: string;
  line: number;
  contra_accounts: { account: string; amount: number; commodity: string }[];
}

export interface ReconciliationTransactionsResponse {
  account: string;
  postings: ReconciliationPosting[];
  total: number;
  limit: number;
  offset: number;
}

export async function getReconciliationSummary(account: string): Promise<ReconciliationSummary> {
  const res = await api.get<ReconciliationSummary>('/reconciliation/summary', { params: { account } });
  return res.data;
}

export async function getReconciliationTransactions(params: {
  account: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ReconciliationTransactionsResponse> {
  const res = await api.get<ReconciliationTransactionsResponse>('/reconciliation/transactions', { params });
  return res.data;
}

export async function markPostings(postings: { file: string; line: number; new_status: string }[]): Promise<{ changed: number }> {
  const res = await api.post<{ changed: number }>('/reconciliation/mark', { postings });
  return res.data;
}

export interface ReconcileResult {
  success: boolean;
  changed: number;
  cleared_balance: number;
  statement_balance: number;
  difference: number;
}

export async function reconcileBatch(data: {
  account: string;
  statement_balance: number;
  posting_ids: { file: string; line: number }[];
}): Promise<ReconcileResult> {
  const res = await api.post<ReconcileResult>('/reconciliation/reconcile', data);
  return res.data;
}

export async function downloadReportPDF(
  reportType: 'dre' | 'balance-sheet' | 'trial-balance',
  params: Record<string, string>,
): Promise<void> {
  const res = await api.get(`/reports/${reportType}/pdf`, {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename=(.+)/);
  link.download = match ? match[1] : `${reportType}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Domínio Sistemas export
// ---------------------------------------------------------------------------

export interface DominioMappingStatus {
  mapped: { account: string; dominio_code: string }[];
  unmapped: string[];
  total_accounts: number;
  total_mapped: number;
}

export interface DominioExportPreview {
  exported: number;
  skipped: number;
  total: number;
  unmapped_accounts: string[];
  mapped_accounts_used: string[];
}

export async function getDominioMapping(): Promise<DominioMappingStatus> {
  const res = await api.get('/reports/dominio/mapping');
  return res.data;
}

export async function updateDominioMapping(mapping: Record<string, string>): Promise<DominioMappingStatus> {
  const res = await api.put('/reports/dominio/mapping', { mapping });
  return res.data;
}

export async function getDominioExportPreview(params: { begin: string; end: string }): Promise<DominioExportPreview> {
  const res = await api.get('/reports/dominio/export', { params });
  return res.data;
}

export async function downloadDominioExport(params: { begin: string; end: string }): Promise<void> {
  const res = await api.get('/reports/dominio/download', {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?(.+?)"?$/);
  link.download = match ? match[1] : `dominio_${params.begin}_${params.end}.txt`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadRegisterPDF(
  account: string,
  begin?: string,
  end?: string,
): Promise<void> {
  const params: Record<string, string> = { account };
  if (begin) params.begin = begin;
  if (end) params.end = end;
  const res = await api.get('/register/pdf', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?(.+?)"?$/);
  link.download = match ? match[1] : `extrato_${account}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Periodic accounts (recurring bills)
// ---------------------------------------------------------------------------

export type PeriodicCadence = 'monthly' | 'yearly' | 'every_n_months';
export type PeriodicStatus = 'paid' | 'pending' | 'overdue' | 'future';

export interface Periodic {
  id: string;
  description: string;
  account: string;
  payment_account: string;
  expected_amount: string | null;
  cadence: PeriodicCadence;
  day_of_month: number;
  month: number | null;
  n_months: number | null;
  start: string;
  end: string | null;
  alert_days_before: number;
  active: boolean;
}

export interface PeriodicOccurrence {
  periodic_id: string;
  description: string;
  account: string;
  payment_account: string;
  due_date: string;
  status: PeriodicStatus;
  expected_amount: string | null;
  payment_date?: string;
  payment_amount?: string;
  payment_payee?: string;
  alert_active: boolean;
}

export async function listPeriodics(): Promise<{ periodics: Periodic[] }> {
  const res = await api.get('/periodics');
  return res.data;
}

export async function getPeriodicStatus(params: {
  from: string;
  to: string;
  include_inactive?: boolean;
}): Promise<{ occurrences: PeriodicOccurrence[] }> {
  const res = await api.get('/periodics/status', { params });
  return res.data;
}

export async function createPeriodic(data: Periodic): Promise<Periodic> {
  const res = await api.post('/periodics', data);
  return res.data;
}

export async function updatePeriodic(id: string, data: Partial<Periodic>): Promise<Periodic> {
  const res = await api.put(`/periodics/${encodeURIComponent(id)}`, data);
  return res.data;
}

export async function deletePeriodic(id: string): Promise<void> {
  await api.delete(`/periodics/${encodeURIComponent(id)}`);
}

export async function getCalendarToken(): Promise<{ token: string | null }> {
  const res = await api.get('/periodics/calendar/token');
  return res.data;
}

export async function regenerateCalendarToken(): Promise<{ token: string }> {
  const res = await api.post('/periodics/calendar/token');
  return res.data;
}

export interface GitSyncState {
  last_push_ok: boolean;
  last_push_at: string | null;
  last_failure_at: string | null;
  last_failure_reason: string | null;
  consecutive_failures: number;
}

export async function getGitSyncState(): Promise<GitSyncState> {
  const res = await api.get<GitSyncState>('/system/git-sync');
  return res.data;
}

export interface EsocialEvent {
  id: string;
  nr_recibo?: string;
  cnpj?: string;
  tp_evt?: string;
  per_apur?: string;
  cpf_trab?: string;
  dh_recepcao?: string;
  ind_retif?: string;
  xml_hash?: string;
  xml_minio_key?: string;
  processed_at?: string;
  ledger_txn_ids?: string;
  created_at?: string;
}

export interface EsocialPosting {
  account: string;
  amount: number;
  commodity: string;
}

export interface EsocialPreviewTransaction {
  action?: 'create' | 'replace';
  date: string;
  description: string;
  postings: EsocialPosting[];
  tags?: Record<string, string>;
  matched_transaction_id?: string;
  matched_date?: string;
  matched_payee?: string;
  matched_file?: string;
}

export interface EsocialParsedEvent {
  event_id: string;
  metadata: Record<string, string>;
  transactions: EsocialPreviewTransaction[];
  payments: { cpf_trab: string; dt_pgto: string; amount: number; source: string }[];
  totals: Record<string, any>;
  warnings: string[];
}

export interface EsocialSyncLog {
  id: string;
  started_at?: string;
  finished_at?: string;
  status?: string;
  per_apur?: string;
  events_found?: string;
  events_downloaded?: string;
  events_stored?: string;
  transactions_created?: string;
  warnings?: string[];
  errors?: string[];
}

export interface EsocialStatus {
  enabled: boolean;
  auto_commit: boolean;
  forbidden_window: boolean;
  should_run_today: boolean;
  today: string;
  next_allowed_date: string;
  latest_sync: EsocialSyncLog | null;
  events_count: number;
  recent_events: EsocialEvent[];
}

export interface EsocialSyncResult {
  status: string;
  per_apur: string;
  force: boolean;
  commit: boolean;
  events_found: number;
  events_downloaded: number;
  events_stored: number;
  transactions_created: number;
  warnings: string[];
  errors: string[];
  events: EsocialEvent[];
  parsed: EsocialParsedEvent[];
}

export interface EsocialCommitResult {
  created: number;
  replaced: number;
  skipped: { index: number; description: string; reason: string }[];
  errors: { index: number; description: string; error: string }[];
  transactions: EsocialPreviewTransaction[];
}

export async function getEsocialStatus(): Promise<EsocialStatus> {
  const res = await api.get<EsocialStatus>('/esocial/status');
  return res.data;
}

export async function listEsocialEvents(params?: {
  per_apur?: string;
  tp_evt?: string;
  limit?: number;
}): Promise<{ events: EsocialEvent[] }> {
  const res = await api.get<{ events: EsocialEvent[] }>('/esocial/events', { params });
  return res.data;
}

export async function syncEsocial(data: {
  per_apur?: string;
  force?: boolean;
  commit?: boolean;
}): Promise<EsocialSyncResult> {
  const res = await api.post<EsocialSyncResult>('/esocial/sync', data);
  return res.data;
}

export async function commitEsocialPreview(data: {
  per_apur: string;
  transactions: EsocialPreviewTransaction[];
}): Promise<EsocialCommitResult> {
  const res = await api.post<EsocialCommitResult>('/esocial/commit', data);
  return res.data;
}

export interface EsocialZipResult {
  total_xml: number;
  stored: number;
  skipped: number;
  errors: string[];
  by_tp_evt: Record<string, number>;
  per_apurs: string[];
}

export async function importEsocialZip(file: File): Promise<EsocialZipResult> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<EsocialZipResult>('/esocial/import-zip', formData);
  return res.data;
}

// === Banco MCP ===
export interface BancoMCPStatus {
  configured: boolean;
  saved_at?: string;
  expires_at?: string;
  valid?: boolean;
}

export async function getBancoMCPStatus(): Promise<BancoMCPStatus> {
  const res = await api.get<BancoMCPStatus>('/banco-mcp/status');
  return res.data;
}

export async function saveBancoMCPToken(mcp_url: string, expires_at?: string) {
  const res = await api.post('/banco-mcp/token', { mcp_url, expires_at: expires_at || '' });
  return res.data;
}

export async function clearBancoMCPToken() {
  const res = await api.delete('/banco-mcp/token');
  return res.data;
}

export async function listBancoMCPTools() {
  const res = await api.get<{ tools: any[] }>('/banco-mcp/tools');
  return res.data;
}

export async function listBancoMCPAccounts() {
  const res = await api.get<{ accounts: any[] }>('/banco-mcp/accounts');
  return res.data;
}

export async function triggerBancoMCPSync(days_back = 30) {
  const res = await api.post('/banco-mcp/sync', { days_back });
  return res.data;
}

export interface BancoMCPSyncStatus {
  running: boolean;
  phase: 'idle' | 'sync' | 'pipeline' | 'done' | 'error';
  started_at: string | null;
  finished_at: string | null;
  days_back: number | null;
  result: {
    accounts?: number;
    txns_new?: number;
    txns_dedup?: number;
    pipeline_error?: string;
  } | null;
  error: string | null;
}

export async function getBancoMCPSyncStatus(): Promise<BancoMCPSyncStatus> {
  const res = await api.get<BancoMCPSyncStatus>('/banco-mcp/sync-status');
  return res.data;
}

export async function bancoMCPAuthenticate(access_token: string) {
  const res = await api.post('/banco-mcp/authenticate', { access_token });
  return res.data;
}

export async function bancoMCPAuthInfo() {
  const res = await api.get<{ text: string; is_error: boolean }>('/banco-mcp/auth-info');
  return res.data;
}

export async function getBancoMCPSchedulerStatus() {
  const res = await api.get('/banco-mcp/scheduler-status');
  return res.data;
}

// --- Reconciliação Banco MCP ---

export interface BancoMCPReconciliationAccount {
  ledger_account: string;
  mcp_id: string | null;
  bank: string | null;
  journal: string | null;
  diff: string | null;
  ok: boolean | null;
  checked_at: string | null;
}

export interface BancoMCPReconciliationTxn {
  id: string;
  date: string;
  amount: string;
  description?: string;
  payee?: string;
}

export interface BancoMCPReconciliationCompare {
  matched: Array<{bank: BancoMCPReconciliationTxn; journal: BancoMCPReconciliationTxn; match_type: string}>;
  bank_only: Array<{bank: BancoMCPReconciliationTxn}>;
  journal_only: Array<{journal: BancoMCPReconciliationTxn}>;
}

export async function getBancoMCPReconciliationStatus(): Promise<BancoMCPReconciliationAccount[]> {
  const res = await api.get<BancoMCPReconciliationAccount[]>('/banco-mcp/reconciliation');
  return res.data;
}

export async function getBancoMCPReconciliationCompare(
  accountKey: string,
  daysBack = 30
): Promise<BancoMCPReconciliationCompare> {
  const res = await api.get<BancoMCPReconciliationCompare>(
    `/banco-mcp/reconciliation/${encodeURIComponent(accountKey)}/compare`,
    { params: { days_back: daysBack } }
  );
  return res.data;
}

export async function deleteBancoMCPJournalTransaction(xactId: string): Promise<void> {
  await api.delete(`/banco-mcp/reconciliation/journal-txn/${encodeURIComponent(xactId)}`);
}

// === NF de fornecedor (recebidas) ===
export interface NFFornecedor {
  txn_id: string;
  date: string;
  payee: string;
  numero: string;
  chave: string;
  cnpj_prestador: string;
  nome_prestador: string;
  competencia: string;
  data_competencia: string;
  data_emissao: string;
  vencimento: string;
  valor: number | null;
  conta_despesa: string;
  conta_passivo: string;
  doc_key: string;
  nsu: string;
  ingest_source: string;
  needs_classification: boolean;
}

export interface NFFornecedorList {
  nfs: NFFornecedor[];
  total: number;
  valor_total: number;
  needs_classification: number;
}

export async function listNFFornecedor(params?: {
  year?: number;
  month?: number;
  cnpj_prestador?: string;
  only_open?: boolean;
}): Promise<NFFornecedorList> {
  const res = await api.get<NFFornecedorList>('/nf-fornecedor', { params });
  return res.data;
}

export async function listNFFornecedorPrestadores() {
  const res = await api.get<{ prestadores: any[]; total: number }>('/nf-fornecedor/prestadores');
  return res.data;
}
